import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Scale, Users, Landmark, HeartCrack, Building2, Check, Trophy } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const IMG = "https://images.unsplash.com/photo-1618044733300-9472054094ee?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const services = [
  { icon: Scale, t: "Challenging Business Valuations", d: "Independent, defensible analysis to test, rebut or support valuations in dispute — from earnings multiples to discounted cash flow and market approaches." },
  { icon: Users, t: "Shareholder & Partnership Disputes", d: "Forensic review of accounts, drawings, dividends and shareholder agreements to clarify entitlements and quantify claims." },
  { icon: Landmark, t: "Taxation Positions", d: "Assessment of tax exposure and reliefs, structuring settlements tax-efficiently and ensuring positions withstand HMRC scrutiny." },
  { icon: HeartCrack, t: "HNW Marital Asset Division", d: "Discreet expert input on identifying, valuing and dividing complex assets — businesses, trusts, pensions and cross-border holdings." },
  { icon: Trophy, t: "Sports & Contractual Matters", d: "Financial analysis behind contractual disputes — valuing contracts, image rights and bonuses and quantifying loss between elite athletes, agents and clubs." },
  { icon: Building2, t: "Post-Resolution Structuring", d: "Designing the corporate and financial architecture that gives settlements clarity, durability and ongoing tax efficiency." },
];

export default function Advisory() {
  return (
    <div className="pt-40">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16,1,0.3,1] }}>
          <div className="overline mb-6">Service 01 · Expert Financial Advisory</div>
          <h1 className="font-display font-light text-5xl lg:text-6xl max-w-4xl leading-[1.05]">Shadow expert advice where the numbers decide the outcome.</h1>
          <p className="text-zinc-400 text-lg mt-8 max-w-2xl leading-relaxed">
            Instructed by lawyers, litigators and counsel, we provide behind-the-scenes financial expertise that sharpens strategy, exposes weaknesses in the opposing case and protects value throughout a dispute.
          </p>
        </motion.div>
        <Reveal delay={0.1}>
          <div className="mt-14 rounded-2xl overflow-hidden border border-white/10 h-[42vh] max-h-[420px]">
            <img src={IMG} alt="Financial analysis" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="overline mb-4">Capabilities</div>
              <h2 className="font-display font-light text-3xl lg:text-4xl leading-tight">Financial expertise, applied to contested matters.</h2>
              <Link to="/contact" data-testid="advisory-enquiry-btn" className="btn-metal inline-flex px-7 py-3.5 rounded-full text-sm font-medium mt-8">Instruct Tactis</Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8 space-y-px bg-white/10 rounded-2xl overflow-hidden">
            {services.map((s, i) => (
              <Reveal key={i} delay={i * 0.05} className="bg-[#0b0b0b]">
                <div className="p-8 flex gap-6 items-start">
                  <s.icon size={26} strokeWidth={1} className="text-zinc-400 shrink-0 mt-1" />
                  <div>
                    <h3 className="font-dash text-xl">{s.t}</h3>
                    <p className="text-zinc-500 mt-2 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#080808] py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <div className="overline mb-5">Why instruct a shadow expert</div>
            <h2 className="font-display font-light text-3xl lg:text-4xl leading-tight">Clarity before the courtroom.</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-12 text-left">
              {["Test the opposing expert's assumptions","Quantify claims and counter-claims","Strengthen instructions to counsel","Protect and structure value post-settlement"].map((p, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0d0d0d] px-5 py-4">
                  <Check size={16} className="text-zinc-300" /> <span className="text-zinc-300 text-sm">{p}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
